import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'utils/dayjs-timezone';
import SearchDateTimePickers from 'common/search-controls/date-time-pickers';
import {Flex, Button, Alert, ModalBody, ModalFooter} from '@spothero/ui';
import useConfig from 'hooks/use-config';
import ReservationAPI from 'api/reservation';
import trackAvailabilityErrorThrown from 'segment/events/availability-error-thrown';
import ErrorUtils from 'utils/error-utils';
import {PROPTYPE_RESERVATION} from 'proptypes/reservation-prop-types';

const ChangeTimesSelect = ({
    initialStarts,
    initialEnds,
    reservation,
    updateStartsAndEnds,
    onCloseModal,
    handleRateDiff,
}) => {
    const {apiDateTimeFormat} = useConfig();
    const {accessKey} = useSelector(state => state.parkingPass);
    const [newStarts, setNewStarts] = useState(initialStarts);
    const [newEnds, setNewEnds] = useState(initialEnds);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [sameTimesError, setSameTimesError] = useState(false);
    const [isInInvalidState, setIsInInvalidState] = useState(false);
    const isAdmin = useSelector(state => state.user.data.isAdmin);

    const fetchRateDiff = async ({
        starts: tentativeStarts,
        ends: tentativeEnds,
    }) => {
        setIsLoading(true);

        let newRateDiff = {};

        try {
            newRateDiff = await ReservationAPI.getChangeTimesRateDifference({
                starts: tentativeStarts,
                ends: tentativeEnds,
                accessKey,
                reservationId: reservation.rentalId,
            });
        } catch (error) {
            trackAvailabilityErrorThrown({
                location: 'Parking Pass',
                errorFetching: true,
                spotId: reservation.facility.id,
                city: reservation?.city?.title,
            });

            // At some point may want to only call this in certain situations / not at all
            ErrorUtils.sendSentryMessage({
                error,
                customErrorMessage: 'Error Fetching Extension Rate Diff',
            });
            setHasError(true);
            setIsLoading(false);

            return;
        }

        if (newRateDiff.available) {
            handleRateDiff(newRateDiff);
            setHasError(false);
            setIsLoading(false);
        } else {
            trackAvailabilityErrorThrown({
                location: 'Parking Pass',
                errorFetching: false,
                spotId: reservation.facility.id,
                city: reservation?.city?.title,
            });

            setHasError(true);
            setIsLoading(false);
        }
    };

    const handleDateTimeChange = ({start, end, isValidStart}) => {
        setIsInInvalidState(false);
        setHasError(false);
        setSameTimesError(false);

        if (!isValidStart) {
            setIsInInvalidState(true);

            return;
        }

        setNewStarts(start.format(apiDateTimeFormat));
        setNewEnds(end.format(apiDateTimeFormat));
    };

    const handleCheckAvailabilityClick = () => {
        if (isLoading) {
            return;
        }

        if (
            dayjs(newStarts)
                .tz(reservation.timezone)
                .isSame(reservation.starts) &&
            dayjs(newEnds).tz(reservation.timezone).isSame(reservation.ends)
        ) {
            setSameTimesError(true);

            return;
        }

        updateStartsAndEnds({
            starts: newStarts,
            ends: newEnds,
        });

        fetchRateDiff({
            starts: newStarts,
            ends: newEnds,
        });
    };

    const buttonDisabled = isInInvalidState || isLoading;

    return (
        <>
            <ModalBody>
                <Flex
                    direction="column"
                    backgroundColor="white"
                    marginX="auto"
                    sx={{
                        '.chakra-alert__title': {
                            fontWeight: 'semibold',
                        },

                        '.FormElement-control .FormElement-item': {
                            borderColor:
                                hasError && !isLoading ? 'error' : null,
                        },
                        '.DatePicker-input-container > span': {
                            width: '100%',
                        },
                    }}
                    data-testid="ChangeTimesSelect"
                    className="ChangeTimesSelect"
                >
                    <SearchDateTimePickers
                        isAdmin={isAdmin}
                        timezone={reservation.timezone}
                        isMonthly={false}
                        starts={initialStarts}
                        ends={initialEnds}
                        isStartReadonly={false}
                        onChange={handleDateTimeChange}
                        parentClassname=".ChangeTimesSelect"
                    />
                    {hasError ? (
                        <Alert
                            marginTop={4}
                            status="error"
                            title="Sorry, the selected times are unavailable"
                        />
                    ) : null}
                    {sameTimesError ? (
                        <Alert
                            marginTop={4}
                            status="error"
                            title="This is already your time slot"
                            description="Your selected dates and times are the same as your current reservation. Choose different dates or times before checking availability."
                        />
                    ) : null}
                </Flex>
            </ModalBody>
            <ModalFooter
                justifyContent="end"
                gap={4}
                flexDirection={{base: 'column', desktop: 'row-reverse'}}
            >
                <Button
                    disabled={buttonDisabled}
                    isLoading={isLoading}
                    onClick={handleCheckAvailabilityClick}
                    loadingText={'Check Availability'}
                    data-testid="ChangeTimesSelect-check-availability-button"
                    width={{base: '100%', desktop: 'auto'}}
                >
                    Check Availability
                </Button>
                <Button
                    width={{base: '100%', desktop: 'auto'}}
                    onClick={onCloseModal}
                    variant="secondary"
                >
                    Cancel
                </Button>
            </ModalFooter>
        </>
    );
};

ChangeTimesSelect.propTypes = {
    initialStarts: PropTypes.string,
    initialEnds: PropTypes.string,
    timezone: PropTypes.string,
    updateStartsAndEnds: PropTypes.func,
    reservation: PROPTYPE_RESERVATION.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    handleRateDiff: PropTypes.func.isRequired,
};

export default ChangeTimesSelect;
